import React, { useEffect, useRef, useState } from 'react'
import brand from '../brand'
import AuthenticationService from '../service/AuthenticationService.js'
import { AuthDivider, apiErrorMessage } from './AuthLayout'

// The Google OAuth Web client id an admin set in Settings ('' when Google sign-in is off). The last answer is
// remembered so login/sign-up draw the right layout at once; every visit still asks the server and updates it.
const STORED_ID = 'googleClientId'

const readStoredId = () => {
    try {
        const id = localStorage.getItem(STORED_ID)
        return id === null ? undefined : id
    } catch (e) {
        return undefined
    }
}

const storeId = (id) => {
    try { localStorage.setItem(STORED_ID, id) } catch (e) { /* private mode: just ask the server each time */ }
}

// After an admin changes Google sign-in in Settings, so the next login page doesn't show the old state
export const forgetGoogleClientId = () => {
    try { localStorage.removeItem(STORED_ID) } catch (e) { /* nothing stored */ }
}

// undefined while unknown (first visit), '' when off, else the client id
export const useGoogleClientId = () => {
    const [clientId, setClientId] = useState(readStoredId)
    useEffect(() => {
        let live = true
        AuthenticationService.googleConfig().then(res => {
            const id = (res.data && res.data.clientId) || ''
            storeId(id)
            if (live) setClientId(id)
        }).catch(() => {
            // server unreachable: keep what we know; a first visit falls back to email only
            if (live) setClientId(known => (known === undefined ? '' : known))
        })
        return () => { live = false }
    }, [])
    return clientId
}

// For class components: <GoogleClientId>{clientId => ...}</GoogleClientId>
export const GoogleClientId = ({ children }) => children(useGoogleClientId())

let gisScript = null
// Google Identity Services, loaded once and only on pages that show the button
const loadGoogleIdentity = () => {
    if (window.google && window.google.accounts && window.google.accounts.id) return Promise.resolve()
    if (!gisScript) {
        gisScript = new Promise((resolve, reject) => {
            const script = document.createElement('script')
            script.src = 'https://accounts.google.com/gsi/client'
            script.async = true
            script.defer = true
            script.onload = resolve
            script.onerror = () => { gisScript = null; reject(new Error('gsi')) }
            document.head.appendChild(script)
        })
    }
    return gisScript
}

const LABELS = { continue_with: 'Continue with Google', signup_with: 'Sign up with Google', signin_with: 'Sign in with Google' }

// Google's "G", only for the placeholder shown until Google's real button has loaded
const GoogleMark = () => (
    <svg viewBox='0 0 48 48' width='18' height='18' aria-hidden='true' focusable='false'>
        <path fill='#EA4335' d='M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z' />
        <path fill='#4285F4' d='M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z' />
        <path fill='#FBBC05' d='M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z' />
        <path fill='#34A853' d='M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z' />
    </svg>
)

// Google's own button (their branding rules require it), drawn over a same-size placeholder and faded in once it
// has settled, so the page never jumps. On success it exchanges Google's ID token for our session token via
// POST /api/auth/google and calls onSuccess(response data). clientId undefined = still asking the server.
const GoogleSignInButton = ({ clientId, text = 'continue_with', onSuccess, onError }) => {
    const holder = useRef(null)
    const [busy, setBusy] = useState(false)
    const [ready, setReady] = useState(false)
    const [unavailable, setUnavailable] = useState(false)
    // keep the latest props without re-drawing Google's button
    const latest = useRef({ text, onSuccess, onError })
    latest.current = { text, onSuccess, onError }

    useEffect(() => {
        if (!clientId) return undefined
        let cancelled = false
        let timer = null
        setReady(false)
        loadGoogleIdentity().then(() => {
            if (cancelled || !holder.current) return
            const gid = window.google.accounts.id
            gid.initialize({
                client_id: clientId,
                ux_mode: 'popup',
                auto_select: false,
                cancel_on_tap_outside: true,
                callback: ({ credential }) => {
                    if (!credential) return
                    setBusy(true)
                    latest.current.onError && latest.current.onError('')
                    AuthenticationService.googleLogin(credential).then(res => {
                        latest.current.onSuccess(res.data)
                    }).catch(error => {
                        setBusy(false)
                        latest.current.onError && latest.current.onError(
                            apiErrorMessage(error, 'We could not sign you in with Google. Please try again.'))
                    })
                }
            })
            const el = holder.current
            el.innerHTML = ''
            gid.renderButton(el, {
                type: 'standard',
                theme: 'outline',
                size: 'large',
                text: latest.current.text,
                shape: brand.shape === 'sharp' ? 'rectangular' : 'pill',
                logo_alignment: 'center',
                width: Math.min(400, Math.max(200, el.offsetWidth || 320))
            })
            // Google first draws a plain button, then adds its iframe (briefly showing both) and removes the plain
            // one once the iframe is ready. Reveal only then; give up waiting after 4 s.
            const started = Date.now()
            const check = () => {
                if (cancelled) return
                const frame = el.querySelector('iframe')
                const settled = frame && frame.offsetHeight > 0 && el.offsetHeight <= 48
                if (settled || Date.now() - started > 4000) {
                    setReady(true)
                    return
                }
                timer = setTimeout(check, 60)
            }
            check()
        }).catch(() => { if (!cancelled) setUnavailable(true) })
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [clientId])

    if (clientId === '') return null
    if (unavailable) {
        return <p className='mv-auth-google-off'>Google sign-in is unavailable right now. Please use your email.</p>
    }
    const cls = 'mv-auth-google' + (ready ? ' is-ready' : '') + (busy ? ' is-busy' : '') + (brand.shape === 'sharp' ? ' is-sharp' : '')
    return (
        <div className={cls} aria-busy={busy || !ready}>
            <div className='mv-auth-google-ph' aria-hidden='true'>
                <GoogleMark /> {LABELS[text] || LABELS.continue_with}
            </div>
            {clientId && <div ref={holder} className='mv-auth-google-btn' />}
            {busy && <span className='mv-auth-google-wait' role='status'>Signing you in…</span>}
        </div>
    )
}

// Google's button plus the "or … with email" divider; nothing when Google sign-in is off. While the server is
// still being asked (first visit), the same space is held by the placeholder so the form below never moves.
export const GoogleSignInOption = ({ clientId, text, divider, onSuccess, onError }) => {
    if (clientId === '') return null
    return (
        <div className='mv-auth-alt'>
            <GoogleSignInButton clientId={clientId} text={text} onSuccess={onSuccess} onError={onError} />
            <AuthDivider>{divider}</AuthDivider>
        </div>
    )
}

export default GoogleSignInButton
